(() => {
  if (window.HSW5PdpSections) return;

  const moduleSelector = '[data-hsw5-module]';

  function formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format((Number(cents) || 0) / 100);
  }

  function getImageCatalog(root) {
    const element = root.querySelector('[data-hsw5-image-catalog]');
    if (!element) return {};
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn('Unable to parse the HSW5 image catalog.', error);
      return {};
    }
  }

  function imageForVariant(catalog, variant) {
    const color = variant?.options?.find((value) => /black|rose gold/i.test(value)) || '';
    const colorKey = /rose gold/i.test(color) ? 'roseGold' : 'black';
    const testVariant = String(window.ABTestHandler?.getVariant?.('hsw5') || '').trim().toLowerCase();
    const imageSet = testVariant === 'watch-only' || testVariant === 'test-watch-only' ? 'watchOnly' : 'default';
    return catalog[imageSet]?.[colorKey] || catalog.default?.[colorKey] || '';
  }

  function initMedia(root) {
    const videos = Array.from(root.querySelectorAll('video'));
    const attemptAutoplay = (video) => {
      video.muted = true;
      video.defaultMuted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      const request = video.play();
      if (request?.catch) request.catch(() => {});
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) attemptAutoplay(video);
          else if (!video.paused) video.pause();
        });
      });
      videos.forEach((video) => observer.observe(video));
    } else {
      videos.forEach(attemptAutoplay);
    }

    root.querySelectorAll('[data-hsw5-ugc-play]').forEach((button) => {
      const figureVideos = Array.from(button.closest('figure')?.querySelectorAll('video') || []);
      if (!figureVideos.length) return;
      const activeVideo = () => figureVideos.find((video) => getComputedStyle(video).display !== 'none') || figureVideos[0];
      button.addEventListener('click', () => {
        const video = activeVideo();
        video.controls = true;
        video.play()?.catch(() => { button.hidden = false; });
      });
      figureVideos.forEach((video) => {
        video.addEventListener('play', () => { button.hidden = true; });
        video.addEventListener('pause', () => { button.hidden = false; });
        video.addEventListener('ended', () => { button.hidden = false; });
      });
    });

    root.querySelectorAll('[data-hsw5-video-sound]').forEach((button) => {
      const figureVideos = Array.from(button.closest('figure')?.querySelectorAll('video') || []);
      if (!figureVideos.length) return;
      const activeVideo = () => figureVideos.find((video) => getComputedStyle(video).display !== 'none') || figureVideos[0];
      button.addEventListener('click', () => {
        const video = activeVideo();
        video.muted = !video.muted;
        button.classList.toggle('is-unmuted', !video.muted);
        button.setAttribute('aria-pressed', String(!video.muted));
        button.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Mute video');
      });
    });
  }

  function initFaqs(root) {
    root.querySelectorAll('[data-hsw5-accordion-group]').forEach((group) => {
      const rows = Array.from(group.querySelectorAll(':scope > [data-hsw5-accordion-row]'));
      rows.forEach((row) => {
        const trigger = row.querySelector('[data-hsw5-accordion-trigger]');
        const content = row.querySelector('[data-hsw5-accordion-content]');
        if (!trigger || !content) return;
        trigger.addEventListener('click', () => {
          const shouldOpen = !row.classList.contains('is-open');
          rows.forEach((otherRow) => {
            const open = otherRow === row && shouldOpen;
            otherRow.classList.toggle('is-open', open);
            otherRow.querySelector('[data-hsw5-accordion-trigger]')?.setAttribute('aria-expanded', String(open));
            const otherContent = otherRow.querySelector('[data-hsw5-accordion-content]');
            if (otherContent) otherContent.hidden = !open;
          });
        });
      });
    });
  }

  function initRepeatBuy(root) {
    const catalog = getImageCatalog(root);
    const image = root.querySelector('.hsw5-pdp__repeat-image');
    const price = root.querySelector('.hsw5-pdp__repeat-buy-price strong');
    const comparePrice = root.querySelector('.hsw5-pdp__repeat-buy-price s');
    const saving = root.querySelector('[data-hsw5-repeat-saving]');
    const addButton = root.querySelector('[data-hsw5-repeat-atc]');
    const addButtonText = addButton?.querySelector('[data-button-text]');
    const optionButtons = Array.from(root.querySelectorAll('[data-hsw5-repeat-option]'));

    root.hsw5RenderVariant = (variant) => {
      if (!variant) return;
      const variantPrice = Number(variant.price) || 0;
      const variantCompare = Number(variant.compare_at_price) || 0;
      const hasCompare = variantCompare > variantPrice;
      if (price) price.textContent = formatMoney(variantPrice);
      if (comparePrice) {
        comparePrice.textContent = hasCompare ? formatMoney(variantCompare) : '';
        comparePrice.hidden = !hasCompare;
      }
      if (saving) {
        saving.textContent = hasCompare ? `Save ${Math.round((variantCompare - variantPrice) * 100 / variantCompare)}%` : '';
        saving.hidden = !hasCompare;
      }
      if (addButton) {
        addButton.disabled = !variant.available;
        if (addButtonText) addButtonText.textContent = variant.available ? `Add to Cart · ${formatMoney(variantPrice)}` : 'Sold out';
      }
      optionButtons.forEach((button) => {
        const matcher = button.dataset.optionKind === 'color' ? /colou?r/i : /size/i;
        const formGroup = Array.from(document.querySelectorAll('[data-hsw5-option-name]')).find((group) => matcher.test(group.dataset.hsw5OptionName));
        const optionIndex = formGroup ? Number(formGroup.dataset.hsw5OptionPosition) - 1 : -1;
        button.classList.toggle('is-active', optionIndex >= 0 && variant.options[optionIndex] === button.value);
      });
      const source = imageForVariant(catalog, variant);
      if (image && source) {
        image.removeAttribute('srcset');
        image.removeAttribute('sizes');
        image.src = source;
      }
    };

    optionButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const matcher = button.dataset.optionKind === 'color' ? /colou?r/i : /size/i;
        const group = Array.from(document.querySelectorAll('[data-hsw5-option-name]')).find((candidate) => matcher.test(candidate.dataset.hsw5OptionName));
        const input = Array.from(group?.querySelectorAll('[data-hsw5-option-value]') || []).find((candidate) => candidate.value === button.value);
        if (!input) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    addButton?.addEventListener('click', () => {
      const primaryButton = document.querySelector(
        '.watch-product-hero[data-section-id] [data-hsw5-atc], .hsw5-pdp[data-section-id] [data-hsw5-atc]'
      );
      if (primaryButton && !primaryButton.disabled) primaryButton.click();
    });
  }

  function initBox(root) {
    const catalog = getImageCatalog(root);
    const image = root.querySelector('.hsw5-pdp__box-image');
    root.hsw5RenderVariant = (variant) => {
      const source = imageForVariant(catalog, variant);
      if (!image || !source) return;
      const color = variant.options.find((value) => /black|rose gold/i.test(value)) || 'Black';
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = source;
      image.alt = `Health Smartwatch 5 ${color} box contents`;
    };
  }

  function initRoot(root) {
    if (!root || root.dataset.hsw5ModuleReady === 'true') return;
    root.dataset.hsw5ModuleReady = 'true';
    if (root.dataset.hsw5Module === 'media-features') initMedia(root);
    if (root.dataset.hsw5Module === 'faqs') initFaqs(root);
    if (root.dataset.hsw5Module === 'repeat-buy') initRepeatBuy(root);
    if (root.dataset.hsw5Module === 'box') initBox(root);
  }

  function scan(scope = document) {
    if (scope.matches?.(moduleSelector)) initRoot(scope);
    scope.querySelectorAll?.(moduleSelector).forEach(initRoot);
  }

  document.addEventListener('hsw5:variant-change', (event) => {
    const testVariant = window.ABTestHandler?.getVariant?.('hsw5');
    document.querySelectorAll(moduleSelector).forEach((root) => {
      if (testVariant) root.dataset.hsw5TestVariant = testVariant;
      else delete root.dataset.hsw5TestVariant;
    });
    document.querySelectorAll(`${moduleSelector}[data-hsw5-module="repeat-buy"], ${moduleSelector}[data-hsw5-module="box"]`).forEach((root) => {
      initRoot(root);
      root.hsw5RenderVariant?.(event.detail?.variant);
    });
  });
  document.addEventListener('shopify:section:load', (event) => scan(event.target));

  window.HSW5PdpSections = { scan };
  scan();
})();
